import { useReactiveVar } from "@apollo/client";
import React, { useEffect, useState } from "react";

import { address } from "/apollo/client/local";
import { AddressState } from "/apollo/client/local/address/address.types";
import { Button, Spinner } from "/component/base";
import { Navigation as NavigationIcon } from "/component/base/Icon";
import Modal from "/component/base/Modal";
import AddressCombobox from "/component/partial/AddressCombobox";
import { useToastContext } from "/component/provider/ToastProvider";
import { getAddressCurrentLocation, updateAddress } from "/geoservice";
import { useTranslation } from "/hook";
import useAmwell from "/hook/useAmwell";
import useAmwellLocation from "/hook/useAmwellLocation";

import { AddressText, CurrentLocationButton } from "./AddressModal.styles";
import { Props } from "./AddressModal.types";

const AddressModal = ({ close, isOpen, dismissable = true, handleDone }: Props) => {
  const { t } = useTranslation("partial-address");
  const { showToast } = useToastContext();

  //Variable storage
  const addressStored = useReactiveVar<AddressState>(address.var);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState<AddressState>();

  //Hooks
  const { consumer, sdk, login, isReady, updateConsumer } = useAmwell();
  const { data: country } = useAmwellLocation();

  useEffect(() => {
    // When we open the modal reset the `selectedAddress` to empty
    if (isOpen) {
      setSelectedAddress(undefined);
    }
  }, [isOpen]);

  //Helper functions
  const handleSubmit = async () => {
    close();

    if (selectedAddress) {
      updateAddress(selectedAddress);

      if (consumer && isReady && sdk?.consumerService && country) {
        const state = country.states.find((state) => state.code === selectedAddress.state);

        if (!state) return;

        if (!consumer.legalResidence || consumer.legalResidence.code !== state.code) {
          await login();
          const consumerUpdate = sdk.consumerService.newConsumerUpdate();
          consumerUpdate.legalResidence = state.code;
          updateConsumer(await sdk.consumerService.updateConsumer(consumer, consumerUpdate));
        }
      }
    }

    if (handleDone) handleDone();
    else close();
  };

  const grabCurrentLocation = async () => {
    setIsLoading(true);

    try {
      const selectedAddress = await getAddressCurrentLocation();
      if (selectedAddress) {
        setSelectedAddress(selectedAddress);
      }
    } catch (error: any) {
      const { friendlyMessage } = error as {
        err: GeolocationPositionError;
        friendlyMessage: string;
      };

      showToast({
        icon: "alert",
        message: friendlyMessage,
        type: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal
      key="AddressModal"
      header={
        <Modal.Header title={t("modal.header")} hideHeaderLeft={!dismissable} close={close} />
      }
      footer={
        <Modal.FooterWithLargeButton>
          <Button fullWidth="percent" onClick={handleSubmit} isDisabled={isLoading}>
            {t("submit")}
          </Button>
        </Modal.FooterWithLargeButton>
      }
      isOpen={isOpen}
      close={dismissable ? close : undefined}
    >
      <AddressCombobox
        formattedAddress={selectedAddress?.formattedAddress || addressStored.formattedAddress}
        placeholder={t("modal.addressPlaceholder")}
        onSelectAddress={(selectedAddress) => {
          setSelectedAddress(selectedAddress);
        }}
      />
      <ul>
        <li>
          <CurrentLocationButton disabled={isLoading} onClick={grabCurrentLocation}>
            <NavigationIcon />
            {isLoading ? (
              <Spinner />
            ) : (
              <AddressText variant="body1Bold">{t("modal.currentLocation")}</AddressText>
            )}
          </CurrentLocationButton>
        </li>
      </ul>
    </Modal>
  );
};

export default AddressModal;
